import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Activity, CheckCircle, AlertTriangle, ChevronDown, ChevronUp, ChevronRight, Sparkles, Zap, RefreshCw, XCircle, Leaf } from 'lucide-react';
import MarkdownText from './ui/MarkdownText';

// ── Status Badge Sub-Component ──
function StatusBadge({ status, t }) {
  // status: 'analyzing' | 'healthy' | 'stress' | 'error'
  const configs = {
    analyzing: {
      icon: Activity,
      label: t.statusAnalyzing,
      bg: 'bg-amber-500/10 dark:bg-amber-500/15',
      border: 'border-amber-500/30',
      text: 'text-amber-600 dark:text-amber-400',
      dot: 'bg-amber-500',
      pulse: true
    },
    healthy: {
      icon: CheckCircle,
      label: t.statusHealthy,
      bg: 'bg-emerald-500/10 dark:bg-emerald-500/15',
      border: 'border-emerald-500/30',
      text: 'text-emerald-600 dark:text-emerald-400',
      dot: 'bg-emerald-500',
      pulse: false
    },
    stress: {
      icon: AlertTriangle,
      label: t.statusStress,
      bg: 'bg-rose-500/10 dark:bg-rose-500/15',
      border: 'border-rose-500/30',
      text: 'text-rose-600 dark:text-rose-400',
      dot: 'bg-rose-500',
      pulse: false
    },
    error: {
      icon: XCircle,
      label: t.errorTitle,
      bg: 'bg-gray-500/10 dark:bg-gray-500/15',
      border: 'border-gray-500/30',
      text: 'text-gray-600 dark:text-gray-400',
      dot: 'bg-gray-500',
      pulse: false
    }
  };

  const config = configs[status] || configs.analyzing;
  const Icon = config.icon;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      className={`inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border ${config.bg} ${config.border} ${config.text}`}
    >
      <span className={`w-2 h-2 rounded-full ${config.dot} ${config.pulse ? 'animate-pulse' : ''}`} />
      <Icon size={14} />
      <span className="text-xs font-bold uppercase tracking-wider">{config.label}</span>
    </motion.div>
  );
}


// ── Main DiagnosticsPanel Component ──
export default function DiagnosticsPanel({
  isStreaming,
  streamContent,
  thinkingContent,
  isThinking,
  isComplete,
  hasError,
  errorMessage,
  onRetry,
  onNewAnalysis,
  t,
  isRTL
}) {
  const dt = t?.diagnostics || {};
  const [isExpanded, setIsExpanded] = useState(true);
  const contentRef = useRef(null);

  // Auto-scroll content while streaming
  useEffect(() => {
    if (isStreaming && contentRef.current) {
      contentRef.current.scrollTop = contentRef.current.scrollHeight;
    }
  }, [streamContent, isStreaming]);

  // Determine status
  let status = 'analyzing';
  if (hasError) status = 'error';
  else if (isComplete) {
    // Simple heuristic: check if content contains stress indicators
    const lower = (streamContent || '').toLowerCase();
    const hasStressKeywords = ['stress', 'إجهاد', 'خطر', 'danger', 'warning', 'تحذير', 'مشكلة', 'problem', 'disease', 'مرض', 'deficiency', 'نقص'].some(kw => lower.includes(kw));
    status = hasStressKeywords ? 'stress' : 'healthy';
  }

  const containerVariants = {
    hidden: { opacity: 0, y: 20, scale: 0.98 },
    visible: {
      opacity: 1, y: 0, scale: 1,
      transition: { type: 'spring', stiffness: 300, damping: 30, staggerChildren: 0.1 }
    },
    exit: { opacity: 0, y: -10, scale: 0.98, transition: { duration: 0.2 } }
  };

  const childVariants = {
    hidden: { opacity: 0, y: 12 },
    visible: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 400, damping: 30 } }
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      className="col-span-12 relative overflow-hidden"
    >
      {/* Main Card */}
      <div className="bg-white dark:bg-slate-800/50 border border-[#1B263B]/10 dark:border-slate-700 rounded-2xl shadow-lg overflow-hidden relative">

        {/* Decorative gradient blobs */}
        <div className="absolute top-0 ltr:right-0 rtl:left-0 w-40 h-40 bg-gradient-to-br from-[#C47A2C]/10 to-[#6B8F71]/10 rounded-full blur-3xl -mt-10 ltr:-mr-10 rtl:-ml-10 pointer-events-none" />
        <div className="absolute bottom-0 ltr:left-0 rtl:right-0 w-32 h-32 bg-gradient-to-tr from-[#6B8F71]/10 to-[#C47A2C]/10 rounded-full blur-3xl -mb-10 ltr:-ml-10 rtl:-mr-10 pointer-events-none" />

        {/* Header */}
        <motion.div variants={childVariants} className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-3 p-5 border-b border-[#1B263B]/5 dark:border-slate-700/50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#C47A2C] to-[#6B8F71] flex items-center justify-center text-white shadow-lg shadow-[#C47A2C]/20">
              <Leaf size={20} />
            </div>
            <div>
              <h3 className="text-base font-bold text-[#1B263B] dark:text-white leading-tight">{dt.title}</h3>
              <p className="text-[0.6rem] font-semibold text-[#6B8F71] dark:text-[#88b58f] uppercase tracking-wider flex items-center gap-1">
                <Zap size={10} />
                {isStreaming ? dt.liveStreaming : (isComplete ? dt.analysisComplete : dt.statusAnalyzing)}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2.5 flex-wrap">
            <StatusBadge status={status} t={dt} />

            {isComplete && (
              <motion.button
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={onNewAnalysis}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-[#C47A2C]/10 hover:bg-[#C47A2C]/15 text-[#C47A2C] text-xs font-semibold transition-all"
              >
                <RefreshCw size={12} />
                {dt.newAnalysis}
              </motion.button>
            )}

            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setIsExpanded(!isExpanded)}
              className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-white/5 text-gray-400 transition-colors"
            >
              {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </motion.button>
          </div>
        </motion.div>

        {/* Expandable Content (Unmounted state prevented to keep text cached) */}
        <motion.div
          initial={false}
          animate={{
            height: isExpanded ? 'auto' : 0,
            opacity: isExpanded ? 1 : 0,
            display: isExpanded ? 'block' : 'none'
          }}
          transition={{ duration: 0.3, ease: 'easeInOut' }}
          className="overflow-hidden"
        >
          {/* Error State */}
          {hasError ? (
            <div className="p-6 text-center">
              <div className="w-14 h-14 mx-auto mb-4 bg-rose-500/10 rounded-2xl flex items-center justify-center">
                <XCircle size={28} className="text-rose-500" />
              </div>
              <h4 className="text-lg font-bold text-[#1B263B] dark:text-white mb-2">{dt.errorTitle}</h4>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4 max-w-md mx-auto">
                {errorMessage || dt.errorDesc}
              </p>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={onRetry}
                className="px-5 py-2 bg-gradient-to-r from-[#C47A2C] to-[#A36322] text-white rounded-xl font-semibold text-sm shadow-lg shadow-[#C47A2C]/20"
              >
                {dt.retryBtn}
              </motion.button>
            </div>
          ) : (
            /* Streaming / Complete Content */
            <div className="relative z-10">
              {/* Live Streaming Indicator */}
              {isStreaming && (
                <div className="px-5 pt-3">
                  <div className="flex items-center gap-2 text-xs font-semibold text-[#C47A2C]">
                    <motion.span
                      animate={{ opacity: [1, 0.3, 1] }}
                      transition={{ repeat: Infinity, duration: 1.5 }}
                      className="w-2 h-2 rounded-full bg-[#C47A2C]"
                    />
                    <Sparkles size={12} />
                    {dt.liveStreaming}
                  </div>
                </div>
              )}

              {/* Content area */}
              <div
                ref={contentRef}
                className="p-5 max-h-[70vh] overflow-y-auto custom-scrollbar"
              >
                <div className="prose prose-sm dark:prose-invert max-w-none
                  prose-headings:text-[#1B263B] dark:prose-headings:text-white
                  prose-h2:text-base prose-h2:font-bold prose-h2:border-b prose-h2:border-[#C47A2C]/15 prose-h2:pb-2 prose-h2:mb-3
                  prose-h3:text-sm prose-h3:font-bold
                  prose-li:marker:text-[#C47A2C]
                  prose-strong:text-[#1B263B] dark:prose-strong:text-white
                  prose-a:text-[#C47A2C]
                ">
                  {/* Thinking Accordion */}
                  {(thinkingContent || isThinking) && (
                    <ThinkingAccordion thinking={thinkingContent} isLive={isThinking} dt={dt} />
                  )}
                  <MarkdownText text={streamContent || ''} />
                </div>

                {/* Typing cursor while streaming */}
                {isStreaming && (
                  <motion.span
                    animate={{ opacity: [1, 0, 1] }}
                    transition={{ repeat: Infinity, duration: 0.8 }}
                    className="inline-block w-2 h-4 bg-[#C47A2C] rounded-sm -mb-0.5 ltr:ml-0.5 rtl:mr-0.5"
                  />
                )}
              </div>

              {/* Footer */}
              {isComplete && (
                <div className="px-5 py-3 border-t border-[#1B263B]/5 dark:border-slate-700/50 flex items-center justify-between">
                  <span className="text-[0.6rem] font-semibold text-gray-400 dark:text-gray-600 uppercase tracking-wider flex items-center gap-1">
                    <Sparkles size={10} />
                    {dt.poweredBy}
                  </span>
                </div>
              )}
            </div>
          )}
        </motion.div>
      </div>
    </motion.div>
  );
}


// ── Thinking Accordion Sub-Component ────────────────────────────────────────

function ThinkingAccordion({ thinking, isLive, dt }) {
  const [expanded, setExpanded] = useState(false);
  const scrollRef = useRef(null);
  const [autoScroll, setAutoScroll] = useState(true);

  // Auto-expand while actively thinking
  useEffect(() => {
    if (isLive) {
      setExpanded(true);
      setAutoScroll(true);
    }
  }, [isLive]);

  // Smooth auto-scroll with delay
  useEffect(() => {
    if (isLive && expanded && autoScroll && scrollRef.current) {
      const timeoutId = setTimeout(() => {
        if (scrollRef.current) {
          scrollRef.current.scrollTo({
            top: scrollRef.current.scrollHeight,
            behavior: 'smooth'
          });
        }
      }, 150);
      return () => clearTimeout(timeoutId);
    }
  }, [thinking, isLive, expanded, autoScroll]);

  // Detect manual scroll up
  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    const isAtBottom = scrollHeight - scrollTop - clientHeight < 15;
    setAutoScroll(isAtBottom);
  };

  return (
    <div className="mb-4 not-prose">
      <motion.button
        whileHover={{ scale: 1.01 }}
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-2 px-3 py-2 bg-[#C47A2C]/[0.06] dark:bg-[#C47A2C]/[0.08] rounded-xl text-xs font-bold text-[#C47A2C] uppercase tracking-wider select-none transition-colors hover:bg-[#C47A2C]/10"
      >
        <motion.div animate={{ rotate: expanded ? 90 : 0 }} transition={{ duration: 0.2 }}>
          <ChevronRight size={14} />
        </motion.div>
        <Sparkles size={12} />
        {isLive ? (dt.thinkingLive || 'Reasoning…') : (dt.thinkingDone || 'Thinking Process')}
        {isLive && (
          <motion.span
            animate={{ opacity: [1, 0.3, 1] }}
            transition={{ repeat: Infinity, duration: 1.5 }}
            className="w-1.5 h-1.5 rounded-full bg-[#C47A2C] ltr:ml-auto rtl:mr-auto"
          />
        )}
      </motion.button>
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden"
          >
            <div 
              ref={scrollRef}
              onScroll={handleScroll}
              className="mt-2 ltr:pl-5 ltr:border-l-2 rtl:pr-5 rtl:border-r-2 border-[#C47A2C]/20 text-xs text-gray-500 dark:text-gray-400 italic whitespace-pre-wrap leading-relaxed max-h-60 overflow-y-auto custom-scrollbar py-1"
            >
              {thinking}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
